import { useState, useEffect } from 'react';
import { logger } from '../utils/logger';
import { Heart, MessageCircle, Send, Trash2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

export const LikeButton = ({ contentType, contentId, size = 'default' }) => {
  const { isAuthenticated } = useAuth();
  const [likes, setLikes] = useState({ count: 0, user_liked: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLikes();
  }, [contentType, contentId]);

  const fetchLikes = async () => {
    try {
      const res = await axios.get(`${API_URL}/likes/${contentType}/${contentId}`);
      setLikes(res.data);
    } catch (error) {
      logger.error('Failed to fetch likes:', error);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to like');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/likes/${contentType}/${contentId}`);
      setLikes({ count: res.data.count, user_liked: res.data.user_liked });
    } catch (error) {
      toast.error('Failed to like');
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = size === 'small' 
    ? 'p-1.5 text-xs gap-1' 
    : 'p-2 text-sm gap-1.5';

  return (
    <button
      onClick={handleLike}
      disabled={loading}
      className={`flex items-center rounded-lg transition-all ${sizeClasses} ${
        likes.user_liked 
          ? 'bg-pink-500/20 text-pink-400' 
          : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-pink-400'
      }`}
      data-testid={`like-btn-${contentType}-${contentId}`}
    >
      <Heart className={`${size === 'small' ? 'w-4 h-4' : 'w-5 h-5'} ${likes.user_liked ? 'fill-current' : ''}`} />
      <span>{likes.count}</span>
    </button>
  );
};

export const CommentSection = ({ contentType, contentId }) => {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [contentType, contentId]);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API_URL}/comments/${contentType}/${contentId}`);
      setComments(res.data);
    } catch (error) {
      logger.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    if (!isAuthenticated) {
      toast.error('Please sign in to comment');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/comments/${contentType}/${contentId}`, {
        text: newComment.trim()
      });
      setComments([res.data.comment, ...comments]);
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await axios.delete(`${API_URL}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const displayedComments = showAll ? comments : comments.slice(0, 5);

  return (
    <div className="space-y-4" data-testid={`comments-${contentType}-${contentId}`}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-purple-400" />
        <h3 className="font-semibold">Comments</h3>
        <span className="text-sm text-gray-500">({comments.length})</span>
      </div>

      {/* Add Comment Form */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            maxLength={1000}
            className="flex-1 p-3 rounded-lg bg-white/5 border border-white/10 focus:border-purple-500 outline-none text-sm"
            data-testid="comment-input"
          />
          <button
            type="submit"
            disabled={submitting || !newComment.trim()}
            className="px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="comment-submit"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
      ) : (
        <p className="text-sm text-gray-500 p-3 bg-white/5 rounded-lg">
          Sign in to leave a comment
        </p>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="spinner"></div>
        </div>
      ) : comments.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-6">No comments yet. Be the first!</p>
      ) : (
        <div className="space-y-3">
          {displayedComments.map(comment => (
            <div 
              key={comment.id} 
              className="p-3 bg-white/5 rounded-lg group"
              data-testid={`comment-${comment.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{comment.user_name}</span>
                    <span className="text-xs text-gray-500">{formatTimeAgo(comment.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-300 break-words">{comment.text}</p>
                </div>
                {(user?.id === comment.user_id || isAdmin) && (
                  <button
                    onClick={() => handleDelete(comment.id)}
                    className="p-1.5 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete comment"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {comments.length > 5 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full py-2 text-sm text-purple-400 hover:text-purple-300"
            >
              {showAll ? 'Show less' : `View all ${comments.length} comments`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const SocialInteractions = ({ contentType, contentId }) => {
  return (
    <div className="flex items-center gap-3">
      <LikeButton contentType={contentType} contentId={contentId} />
    </div>
  );
};
